import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type UIEvent,
} from "react";
import {
  isRunKeyboardShortcut,
  lineNumberLabels,
} from "@/lib/sql-runner";
import { sqlTokenClassName, tokenizeSQL } from "@/lib/sql-highlight";
import { cn } from "@/lib/utils";

export type SqlCodeEditorProps = {
  value: string;
  onChange: (value: string) => void;
  /** Invoked on Ctrl/Cmd+Enter — same intent as the Run button. */
  onRun?: () => void;
  height?: number | string;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
};

const EDITOR_FONT: CSSProperties = {
  fontFamily: "var(--font-mono), ui-monospace, monospace",
  fontSize: 13,
  lineHeight: "20px",
  tabSize: 2,
};

/**
 * Self-hosted SQL editor: line numbers, syntax highlighting, Ctrl/Cmd+Enter run.
 * No Monaco / CDN loaders — pure React + in-repo tokenizer.
 */
export function SqlCodeEditor({
  value,
  onChange,
  onRun,
  height = 280,
  disabled,
  className,
  "aria-label": ariaLabel = "SQL editor",
}: SqlCodeEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const lines = lineNumberLabels(value);
  const tokens = tokenizeSQL(value.endsWith("\n") ? value + " " : value);

  const syncScroll = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = ta.scrollTop;
      highlightRef.current.scrollLeft = ta.scrollLeft;
    }
    if (gutterRef.current) {
      gutterRef.current.scrollTop = ta.scrollTop;
    }
  }, []);

  useEffect(() => {
    syncScroll();
  }, [value, syncScroll]);

  function handleScroll(e: UIEvent<HTMLTextAreaElement>) {
    const ta = e.currentTarget;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = ta.scrollTop;
      highlightRef.current.scrollLeft = ta.scrollLeft;
    }
    if (gutterRef.current) {
      gutterRef.current.scrollTop = ta.scrollTop;
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (isRunKeyboardShortcut(e) && onRun && !disabled) {
      e.preventDefault();
      onRun();
      return;
    }
    // Tab inserts two spaces (common SQL editor affordance)
    if (e.key === "Tab" && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      const ta = e.currentTarget;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = value.slice(0, start) + "  " + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
    }
  }

  const heightStyle =
    typeof height === "number" ? `${height}px` : height;

  return (
    <div
      className={cn(
        "relative flex overflow-hidden bg-code-block-bg",
        className,
      )}
      style={{ height: heightStyle }}
      data-testid="sql-code-editor"
    >
      {/* Line numbers */}
      <div
        ref={gutterRef}
        className="shrink-0 select-none overflow-hidden border-r border-code-block-border bg-[#161616] py-3 text-right"
        aria-hidden
        data-testid="sql-line-numbers"
      >
        <div style={{ ...EDITOR_FONT, paddingRight: 10, paddingLeft: 8 }}>
          {lines.map((n) => (
            <div
              key={n}
              className="text-[#6e6e6e]"
              style={{ height: 20 }}
            >
              {n}
            </div>
          ))}
        </div>
      </div>

      <div className="relative min-w-0 flex-1">
        {/* Highlight layer */}
        <pre
          ref={highlightRef}
          className="pointer-events-none absolute inset-0 m-0 overflow-auto whitespace-pre p-3 text-code-block-text"
          style={EDITOR_FONT}
          aria-hidden
          data-testid="sql-highlight"
        >
          <code>
            {tokens.map((t, i) => {
              const cls = sqlTokenClassName(t.type);
              if (!cls && t.type === "ws") {
                return <span key={`${reactId}-t-${i}`}>{t.value}</span>;
              }
              return (
                <span key={`${reactId}-t-${i}`} className={cls || undefined}>
                  {t.value}
                </span>
              );
            })}
          </code>
        </pre>

        {/* Editable layer */}
        <textarea
          ref={textareaRef}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          aria-label={ariaLabel}
          data-testid="sql-textarea"
          className={cn(
            "absolute inset-0 z-10 m-0 h-full w-full resize-none overflow-auto",
            "border-0 bg-transparent p-3 text-transparent caret-[#f3f3f3]",
            "outline-none focus:outline-none focus-visible:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
          style={{
            ...EDITOR_FONT,
            // Keep caret visible; selection still usable via browser default
            WebkitTextFillColor: "transparent",
          }}
        />
      </div>
    </div>
  );
}
